import { useState } from 'react';
import Image from 'next/future/image';
import cn from 'classnames';
import infoIconBlack from '@/public/icons/product/infoIconBlack.svg';
import { DesktopProps, MobileProps, ReebeloCareSectionProps } from './types';
import { priceFormater } from '@/settings/index';
import settings from '@/settings';
import Icon from './Icon';
import RecommendedBadge from './RecommendedBadge';
import PriceInformation from './PriceInformation';
import Toggle from './Toggle';
import Title from './Title';
import Details from './Details';
import AdditionalInfo from './AdditionInfo';
import DeviceImage from './DeviceImage';
import { reebelocareProtectionTitle } from './constants';

const isAuStore = settings.store === 'reebelo-au';

const Mobile = ({
  showModal,
  monthlyPrice,
  reebelocareProtection,
  onClick,
  isActive,
  price,
}: MobileProps) => (
  <section className="sm:hidden">
    <div
      className={cn(
        'relative mt-1 flex gap-3 rounded-lg border text-xs transition duration-150 ease-in-out',
        {
          'p-3': !isAuStore,
          'p-6 pr-3': isAuStore,
        },
      )}
      // eslint-disable-next-line react/forbid-dom-props
      style={{
        background:
          'linear-gradient(90deg, #ECFCF5 0%, #ECFCF5 50%, #FFF 100%)',
      }}
    >
      <div className="flex w-full items-center gap-5">
        <div className="flex-1">
          <div className="flex w-full items-center gap-3">
            <Icon />
            <div className="flex-1">
              <div className="flex flex-1 items-start justify-between gap-2 xs:mb-2">
                <RecommendedBadge />
                <div className="flex items-center gap-2">
                  <PriceInformation
                    price={price}
                    monthlyPrice={monthlyPrice}
                    className="hidden xs:block"
                  />
                  <Toggle onClick={onClick} isActive={isActive} />
                </div>
              </div>
              <PriceInformation
                price={price}
                monthlyPrice={monthlyPrice}
                className="mt-1.5 block xs:hidden"
              />
              <Title
                reebelocareProtection={reebelocareProtection}
                className="mt-2 hidden xs:block"
              />
            </div>
          </div>
          <Title
            reebelocareProtection={reebelocareProtection}
            className="mt-2 xs:hidden "
          />
          <Details />
          <AdditionalInfo showModal={showModal} />
        </div>
      </div>
    </div>
  </section>
);

const Desktop = ({
  showModal,
  monthlyPrice,
  imageUrl,
  reebelocareProtection,
  onClick,
  isActive,
  price,
}: DesktopProps) => (
  <section className="hidden sm:block">
    <div
      className={cn(
        'relative mt-1 flex gap-3 rounded-lg border text-xs transition duration-150 ease-in-out',
        {
          'p-3': !isAuStore,
          'px-3 py-6': isAuStore,
        },
      )}
      // eslint-disable-next-line react/forbid-dom-props
      style={{
        background:
          'linear-gradient(90deg, #ECFCF5 0%, #ECFCF5 50%, #FFF 100%)',
      }}
    >
      <div className="flex w-full items-center gap-5">
        <DeviceImage imageUrl={imageUrl} />
        <div className="flex-1">
          <div className="flex w-full items-center gap-3">
            <div className="flex flex-1 items-start justify-between gap-2 xxs:mb-2">
              <RecommendedBadge />
              <div className="flex items-center gap-2">
                <PriceInformation price={price} monthlyPrice={monthlyPrice} />
                <Toggle onClick={onClick} isActive={isActive} />
              </div>
            </div>
          </div>
          <Title reebelocareProtection={reebelocareProtection} />
          <Details />
          <AdditionalInfo showModal={showModal} />
        </div>
      </div>
    </div>
  </section>
);

export default function ReebeloCareSection({
  price,
  isActive,
  onClick,
  imageUrl,
  Modal,
  duration,
}: Readonly<ReebeloCareSectionProps>) {
  const [isModalVisible, setModalVisibility] = useState(false);

  const generateReebelocareProtection = () =>
    reebelocareProtectionTitle[settings.store] ??
    reebelocareProtectionTitle.default;

  const reebelocareProtection = generateReebelocareProtection();

  const hideModal = () => setModalVisibility(false);

  const showModal = () => {
    setModalVisibility(true);
  };

  const sectionTitle = isAuStore ? 'Add ReebeloCare' : 'Add a Protection Plan';
  const monthlyPrice = priceFormater.long(price / duration);

  return (
    <div>
      {/* Header */}
      <button className="flex items-center gap-1.5" onClick={() => showModal()}>
        <h4 className="mb-1 flex text-xxs font-bold uppercase text-gray-700 lg:font-normal">
          {sectionTitle}
        </h4>
        <div className="-mt-1.5 h-3 w-3">
          <Image
            src={infoIconBlack}
            alt="Condition"
            width={12}
            height={12}
            className="h-full w-full object-contain"
          />
        </div>
      </button>
      {/* Card */}
      <Desktop
        showModal={showModal}
        monthlyPrice={monthlyPrice}
        imageUrl={imageUrl}
        reebelocareProtection={reebelocareProtection}
        onClick={onClick}
        isActive={isActive}
        price={price}
      />
      <Mobile
        showModal={showModal}
        monthlyPrice={monthlyPrice}
        reebelocareProtection={reebelocareProtection}
        onClick={onClick}
        isActive={isActive}
        price={price}
      />
      {Modal && (
        <Modal
          isVisible={isModalVisible}
          hideModal={hideModal}
          warrantyPurchased={isActive}
          purchaseWarranty={onClick}
          price={price}
          duration={duration}
        />
      )}
    </div>
  );
}
